import type { PortalNotification, PortalNotificationsResult } from '@erp/shared';
import { Bell, CheckCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, Card, EmptyState, Skeleton } from '../../components/ui/index.js';
import { customerApi } from '../../lib/customerApi.js';
import { formatDate } from '../../lib/intl.js';

export function PortalNotificationsPage(): JSX.Element {
  const { t, i18n } = useTranslation('portal');
  const navigate = useNavigate();
  const [data, setData] = useState<PortalNotificationsResult | null>(null);

  const load = (): void => {
    void customerApi()
      .get<PortalNotificationsResult>('/notifications', { params: { limit: 100 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0, unreadCount: 0 }));
  };
  useEffect(load, []);

  const open = (n: PortalNotification): void => {
    if (!n.isRead) void customerApi().post(`/notifications/${n.id}/read`, {}).then(load).catch(() => undefined);
    if (n.relatedType === 'invoice' && n.relatedId) navigate(`/portal/invoices/${n.relatedId}`);
    else if (n.relatedType === 'payment' && n.relatedId) navigate(`/portal/payments/${n.relatedId}`);
    else if (n.relatedType === 'sales_order' && n.relatedId) navigate(`/portal/orders/${n.relatedId}`);
  };

  // Order notifications are server-authored language-neutrally (title = order
  // number, status in the `type` suffix) so the inbox can localise them here.
  const notifText = (n: PortalNotification): { heading: string; sub: string | null } => {
    if (n.type === 'order.received') {
      return { heading: t('notifications.orderReceived'), sub: t('notifications.orderRef', { n: n.title }) };
    }
    if (n.type.startsWith('order.status.')) {
      const status = n.type.slice('order.status.'.length);
      return {
        heading: t('notifications.orderStatusTitle'),
        sub: t('notifications.orderStatusBody', { n: n.title, status: t(`notifications.status.${status}`, { defaultValue: status }) }),
      };
    }
    return { heading: n.title, sub: n.body };
  };

  const markAll = (): void => {
    void customerApi().post('/notifications/read-all', {}).then(load).catch(() => undefined);
  };

  return (
    <div className="flex flex-col gap-4 pb-16 lg:pb-0">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('notifications.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('notifications.subtitle')}</p>
        </div>
        {(data?.unreadCount ?? 0) > 0 && (
          <Button variant="secondary" onClick={markAll}>
            <CheckCheck className="me-1.5 h-4 w-4" aria-hidden />
            {t('notifications.markAllRead')}
          </Button>
        )}
      </header>

      {!data ? (
        <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>
      ) : data.items.length === 0 ? (
        <EmptyState icon={<Bell className="h-8 w-8" aria-hidden />} title={t('notifications.empty')} description={t('notifications.emptyDescription')} />
      ) : (
        <Card className="overflow-hidden p-0">
          <ul>
            {data.items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => open(n)}
                  className={`flex w-full items-start gap-3 border-t border-border px-4 py-3 text-start transition first:border-t-0 hover:bg-bg-subtle ${n.isRead ? '' : 'bg-brand/5'}`}
                >
                  <span className={`mt-1.5 h-2 w-2 flex-none rounded-full ${n.isRead ? 'bg-transparent' : 'bg-brand'}`} aria-hidden />
                  <span className="flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className={`text-sm ${n.isRead ? 'text-fg' : 'font-semibold text-fg'}`}>{notifText(n).heading}</span>
                      <span className="flex-none text-xs text-fg-muted">{formatDate(n.createdAt, { locale: i18n.language })}</span>
                    </span>
                    {notifText(n).sub && <span className="mt-0.5 block text-sm text-fg-muted">{notifText(n).sub}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
